/** Path under fx `/vaerk/tekniker-login` uden hash; undgår `not-tekniker-login`. */
function pathnameHasTeknikerLoginSegment(pathname: string): boolean {
    return /(^|\/)tekniker-login(\/|$)/.test(pathname || "");
}

/** ServicePro NUI login (path routing uden hash). */
function pathnameHasServiceproLoginSegment(pathname: string): boolean {
    return /(^|\/)servicepro-login(\/|$)/.test(pathname || "");
}

/** Deployet apps/servicepro (path routing) på kendte domæner; ikke localhost (håndteres særskilt). */
function isServiceproStandaloneHostname(hostname: string): boolean {
    const h = (hostname || "").toLowerCase();
    if (!h || h === "localhost" || h.startsWith("127.")) {
        return false;
    }
    if (!h.includes("servicepro")) {
        return false;
    }
    return h.endsWith("eforsyning.dk") || h.endsWith("softvaerket.dk") || h.endsWith("servicepro.dk");
}

export function isTeknikerContext(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    const hostname = (window.location.hostname || "").toLowerCase();
    const port = (window.location.port || "").trim();
    // `nx run servicepro:serve` (port 4242) – kun tekniker-login; undgår redirect-loop /login ⟷ /tekniker-login
    if ((hostname === "localhost" || hostname === "127.0.0.1") && port === "4242") {
        return true;
    }

    const pathname = window.location.pathname || "";
    const hash = window.location.hash || "";
    if (
        pathnameHasTeknikerLoginSegment(pathname) ||
        pathnameHasServiceproLoginSegment(pathname) ||
        hash.startsWith("#/tekniker-login") ||
        hash.startsWith("#/servicepro-login")
    ) {
        return true;
    }

    const requestedMode = getRequestedMode();
    if (requestedMode === "tekniker") {
        return true;
    }
    if (requestedMode === "forbruger") {
        return false;
    }

    const isTeknikerSubdomain =
        hostname &&
        hostname !== "localhost" &&
        !hostname.startsWith("127.") &&
        (hostname.startsWith("teknik.") || hostname.startsWith("tekniker.") || hostname.startsWith("dev-teknik."));

    return isTeknikerSubdomain || isServiceproStandaloneHostname(hostname) || hasTeknikerQueryParam();
}

export function hasTeknikerQueryParam(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    const search = getSearchFromLocation();
    if (!search) {
        return false;
    }

    return getRequestedMode() === "tekniker";
}

function getRequestedMode(): "tekniker" | "forbruger" | null {
    const search = getSearchFromLocation();
    if (!search) {
        return null;
    }

    const params = new URLSearchParams(search);
    const teknikerParam = params.get("tekniker");
    if (teknikerParam !== null) {
        if (teknikerParam === "" || teknikerParam === "1" || teknikerParam.toLowerCase() === "true") {
            return "tekniker";
        }
        if (teknikerParam === "0" || teknikerParam.toLowerCase() === "false") {
            return "forbruger";
        }
    }

    const appParam = params.get("app") || params.get("mode") || params.get("role");
    if (!appParam) {
        return null;
    }

    const normalized = appParam.toLowerCase();
    if (normalized === "tekniker") {
        return "tekniker";
    }
    if (normalized === "forbruger" || normalized === "consumer") {
        return "forbruger";
    }

    return null;
}

function getSearchFromLocation(): string {
    if (typeof window === "undefined") {
        return "";
    }

    const search = window.location.search || "";
    if (search) {
        return search;
    }

    const hash = window.location.hash || "";
    const queryIndex = hash.indexOf("?");
    return queryIndex >= 0 ? hash.slice(queryIndex) : "";
}
